// ============================================================================
// L'ONGLET « PHOTOS » de la fiche article — relevé maquette l.3833-3910.
//
// ⚠️ DEUX NOTIONS, ET LES CONFONDRE EST LE PIÈGE DE CET ÉCRAN (correction 2026-08-20) :
//   · LE PREMIER DE CHAQUE CORLORIS — son visuel principal, en caisse et sur le site. Il y en a
//     autant que de couleurs, et c'est l'ORDRE qui le désigne : on le change en glissant.
//   · L'ÉTOILE — LA photo du MODÈLE, la vignette de la liste d'articles. Une seule pour tout
//     l'article, quel que soit le coloris. C'est un DRAPEAU : elle ne déplace rien, et recliquer
//     dessus la retire (« Photo de la fiche — cliquer pour retirer », maquette l.16483).
// Les fondre déplacerait le visuel d'une couleur chaque fois qu'on choisit la photo de fiche.
//
// ⚠️ LE GLISSER VIENT DU MODULE PARTAGÉ (`useTileDrag`, axe `xy`) — pas une seconde implémentation.
// Il porte déjà le seuil de 5 px, le fantôme cloné, le creux, le FLIP des voisines pendant le survol
// et l'annulation par Échap. Réécrire un glisser, c'est réécrire ces oublis-là.
//
// ⚠️ L'ORDRE S'ENREGISTRE AU DÉPÔT, PAS AU PIED DE LA FICHE. Ce bloc a sa propre écriture, comme le
// prix par déclinaison : un « Enregistrer » commun laisserait croire qu'on peut abandonner un
// réordonnancement déjà appliqué.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { confirm, Picker, Tooltip, Trans, useLatest, useTileDrag } from '@orgazone/ui-web';
import { api, type ArticlePhoto } from '../api';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { CIBLE_PX, motifNonPreparable, poidsLisible, preparerPhoto } from './photo-preparation';

const SANS_COLORIS = '—';

/**
 * Combien de coloris restent LISIBLES en pastilles.
 * ⚠️ Quatre parce que c'est ce qui tient sur UNE ligne à la largeur de la fiche : au cinquième, les
 * pastilles se replient et repoussent la galerie hors de l'écran — le contraire de ce qu'on est venu
 * faire ici. Au-delà, un seul contrôle et une recherche.
 */
const SEUIL_PASTILLES = 4;

/**
 * Limites ANNONCÉES à l'écran. ⚠️ CE SONT CELLES DE LA PRÉPARATION, pas celles du serveur : c'est ce
 * que l'opérateur obtient réellement, et donc la seule chose qu'on puisse lui promettre. Le mur du
 * serveur (2000 px / 1 Mo) est volontairement plus haut — il n'attrape que l'anormal, et l'annoncer
 * ferait croire qu'il faut viser 2000 px alors que l'écran ramène tout à 1600.
 */
const PHOTO_MAX_KO = 500;
const PHOTO_CIBLE_PX = CIBLE_PX;
/** Le mur du serveur, en octets — la préparation vise plus bas, ce plafond ne sert qu'au repli. */
const PLAFOND_SERVEUR_OCTETS = 1024 * 1024;

export function PhotosArticle({ modelKey, couleurs, editable, onChanged }: {
  modelKey: string;
  /** Les coloris RÉELS du modèle — les pastilles ne s'inventent pas, elles viennent des déclinaisons. */
  couleurs: string[];
  editable: boolean;
  /**
   * Prévient que les photos du modèle ont changé — la LISTE derrière doit se relire.
   *
   * ⚠️ SANS LUI, RIEN NE REDESCEND (correctif 2026-08-25). Cet onglet écrit directement au serveur,
   * hors du cycle « modifier / enregistrer » de la fiche : il ne passait donc jamais par `onChanged`,
   * et la liste gardait sa page telle qu'elle l'avait lue. Vu de l'écran : on désigne la photo de
   * fiche, le serveur l'enregistre, et la vignette de la liste ne bouge pas — l'écran contredit une
   * écriture qui a bel et bien eu lieu (§23.0).
   */
  onChanged?: () => void;
}) {
  const [rows, setRows] = useState<ArticlePhoto[] | null>(null);
  const [max, setMax] = useState(6);
  const [failure, setFailure] = useState<string | null>(null);
  const [coloris, setColoris] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  /** Le compte rendu de la préparation — ce que l'envoi a réellement transformé. */
  const [prepare, setPrepare] = useState<string | null>(null);
  const champ = useRef<HTMLInputElement | null>(null);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA GALERIE : le nettoyage de l'effet ne garde que son échec, pas les vignettes
  //    que `reload` pose lui-même — les photos du modèle précédent, revenues en dernier, s'afficheraient sous le nouveau,
  //    et un dépôt les rangerait parmi elles.
  const latestPhotos = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latestPhotos();
    const r = await api.articlePhotos(modelKey);
    if (!isLatest()) return;
    setRows(r.rows);
    setMax(r.maxPerColor);
  }, [modelKey, latestPhotos]);

  useEffect(() => {
    let vivant = true;
    reload().then(() => { if (vivant) setFailure(null); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [reload]);

  // ⚠️ LE PREMIER CORLORIS RÉEL, jamais un « tous » implicite : une photo appartient à un coloris, et
  // en déposer une sans savoir lequel produirait un visuel qu'aucune déclinaison ne montre.
  useEffect(() => {
    if (coloris === null && couleurs.length > 0) setColoris(couleurs[0]!);
  }, [couleurs, coloris]);

  const duColoris = useMemo(
    () => (rows ?? []).filter((p) => (p.color ?? null) === (coloris ?? null)).sort((a, b) => a.rank - b.rank),
    [rows, coloris],
  );
  const parColoris = useMemo(() => {
    const n = new Map<string, number>();
    for (const p of rows ?? []) n.set(p.color ?? SANS_COLORIS, (n.get(p.color ?? SANS_COLORIS) ?? 0) + 1);
    return n;
  }, [rows]);

  const reste = max - duColoris.length;

  /** Enregistre l'ordre — et donc le visuel principal. Une seule route pour les deux intentions. */
  const arranger = useCallback(async (ordre: string[]) => {
    // ⚠️ OPTIMISTE PUIS RELU : le geste doit répondre tout de suite, mais c'est le SERVEUR qui décide
    // de l'ordre final. Sans la relecture, un refus laisserait l'écran montrer un ordre inexistant.
    setRows((prev) => (prev ?? []).map((p) => {
      const i = ordre.indexOf(p.id);
      return i < 0 ? p : { ...p, rank: i };
    }));
    try {
      await api.arrangeArticlePhotos(modelKey, coloris, ordre);
      await reload();
      onChanged?.();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
      await reload().catch(() => {});
    }
  }, [modelKey, coloris, reload, onChanged]);

  /**
   * Retire un visuel de la fiche.
   * ⚠️ CONFIRMÉ, parce que c'est IRRÉVERSIBLE côté fiche : le fichier d'origine n'est plus sur le poste
   * de celui qui l'avait déposé, et le retrouver suppose de savoir où il a été pris.
   * ⚠️ IL EFFACE AUSSI LES OCTETS, mais SOUS CONDITIONS — commentaire corrigé le 2026-08-25, il disait
   * l'inverse depuis la décision du 2026-08-22. Le serveur retire l'objet du stockage quand il
   * appartient à ce magasin ET que plus aucune ligne ne le pointe. Les deux réserves viennent de
   * l'adressage par CONTENU : une clé est l'empreinte de l'image, donc la même photo posée sur deux
   * coloris n'est qu'UN objet — l'effacer parce qu'un lien disparaît casserait l'autre fiche. Et un
   * visuel de la bibliothèque commune appartient à tous les clients, jamais à celui qui le retire.
   */
  const retirer = useCallback(async (id: string) => {
    const ok = await confirm({
      title: t('Retirer cette photo ?'),
      message: t('Elle disparaitra de la fiche, de la caisse et du site. Le fichier d’origine, lui, n’est plus sur votre poste.'),
      confirmLabel: t('Retirer'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.removeArticlePhoto(modelKey, coloris, id);
      await reload();
      onChanged?.();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [modelKey, coloris, reload, onChanged]);

  /**
   * Bascule LA photo du modele — la vignette de la liste d articles.
   * ⚠️ ELLE NE DEPLACE RIEN : le visuel de chaque coloris reste le premier de son coloris. Et elle se
   * RETIRE en recliquant (maquette l.16540) — sans quoi un mauvais choix serait definitif tant qu on
   * n en designe pas un autre.
   * ⚠️ Le drapeau vaut pour TOUT l article : marquer une photo demarque celle d avant, meme si elle
   * appartient a un autre coloris. C est le serveur qui s en charge, sur le modele entier.
   */
  const toggleSheetPhoto = useCallback(async (id: string) => {
    try {
      await api.setArticleMainPhoto(modelKey, id);
      await reload();
      onChanged?.();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [modelKey, reload, onChanged]);

  const drag = useTileDrag({
    keys: duColoris.map((p) => p.id),
    onReorder: (next) => { void arranger(next); },
    axis: 'xy',
    disabled: !editable || envoi,
    ignoreSelector: '[data-no-drag]',
  });

  const deposer = async (fichiers: FileList | null) => {
    if (!fichiers || fichiers.length === 0) return;
    setEnvoi(true);
    setFailure(null);
    setPrepare(null);
    try {
      const lot = Array.from(fichiers);
      // ⚠️ CE QU AUCUN NAVIGATEUR NE SAIT OUVRIR SE DIT AVANT (HEIC) : laisser la preparation echouer
      //    puis le serveur refuser donnerait un message technique sur un cas parfaitement previsible.
      const bloquant = lot.map(motifNonPreparable).find((m) => m !== null);
      if (bloquant) throw new Error(bloquant);

      // ⚠️ PREPARATION AVANT ENVOI (2026-08-25) : 1600 px, WebP. Sans elle, une photo de telephone
      //    (4032 px, 2 a 5 Mo) serait refusee par le mur du serveur, et l operateur n aurait aucun
      //    moyen de la reduire depuis son telephone — la limite deviendrait un mur, pas une regle.
      const preparees = [];
      for (const f of lot) preparees.push(await preparerPhoto(f, PLAFOND_SERVEUR_OCTETS));
      const retouchees = preparees.filter((p) => p.retouchee);

      await api.addArticlePhotos(modelKey, coloris, preparees.map((p) => p.fichier), preparees.map((p) => p.vignette));
      // ⚠️ CE QUI A ETE FAIT SE DIT : une photo envoyee n est pas la photo choisie, et le taire
      //    laisserait croire que l original est en base (§23.0).
      if (retouchees.length > 0) {
        const avant = retouchees.reduce((total, p) => total + p.octetsAvant, 0);
        const after = retouchees.reduce((total, p) => total + p.octetsApres, 0);
        // ⚠️ UNE SEULE CLÉ, PAS UNE MOITIÉ TRADUITE COLLÉE À UNE MOITIÉ FRANÇAISE : le morceau
        //    « à … px — … → … » restait en français quoi qu'il arrive, et le traducteur recevait une
        //    phrase sans sa fin.
        setPrepare(t('{n} image redimensionnée à {cible} px — {avant} → {apres}.', {
          n: retouchees.length, cible: PHOTO_CIBLE_PX, avant: poidsLisible(avant), apres: poidsLisible(after),
        }));
      }
      await reload();
      onChanged?.();
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL, jamais un « échec » générique : « format non reconnu » et « maximum atteint »
      // appellent deux gestes différents, et l'opérateur doit savoir lequel.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setEnvoi(false);
      if (champ.current) champ.current.value = ''; // sinon le même fichier ne se redépose pas
    }
  };

  const byId = new Map(duColoris.map((p) => [p.id, p]));

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 210 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Photos')}</div>
          <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            {t('{n} visuels par coloris — la première photo du coloris est le visuel principal en caisse et sur le site.', { n: max })}{' '}
            <Trans
              k="<1>{coloris}</1> : <2>{compte}</2>/{max}."
              vars={{ coloris: coloris ?? SANS_COLORIS, compte: duColoris.length, max }}
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]}
            />
          </p>
        </div>
        {editable && (reste > 0
          ? (
            <label style={{
              height: 36, padding: '0 13px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', color: 'var(--teal-700)', fontSize: '12.5px', fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M12 5v14M5 12h14" />
              </svg>
              {envoi ? t('Envoi…') : t('Ajouter des images')}
              <input
                ref={champ} type="file" accept="image/*" multiple disabled={envoi}
                onChange={(e) => void deposer(e.target.files)} style={{ display: 'none' }}
              />
            </label>
          )
          : (
            <span style={{
              height: 36, padding: '0 13px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)',
              color: 'var(--ink-500)', fontSize: '12.5px', fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap',
            }}>{t('Maximum atteint')}</span>
          ))}
      </div>

      {/* ⚠️ AU-DELÀ DE QUATRE COLORIS, UNE LISTE — PAS DES PASTILLES (retour terrain 2026-08-25 :
          « certains produits ont 335 déclinaisons à cause de 30 couleurs, c'est inexploitable »).
          Les pastilles sont le bon outil tant qu'on les embrasse d'un regard : elles montrent TOUT et
          se choisissent d'un clic. Passé une poignée, elles se replient sur cinq rangs, poussent la
          galerie hors de l'écran, et il faut lire trente étiquettes pour en trouver une. La liste
          inverse le rapport : un seul contrôle, et on TAPE le nom du coloris. Le décompte n/6 la
          suit — c'est la seule question qu'on se pose ici, et elle ne doit pas se perdre au passage. */}
      {couleurs.length > SEUIL_PASTILLES && (
        <div style={{ maxWidth: 340, marginBottom: 13 }}>
          <Picker
            id="bo-photos-coloris"
            variant="field"
            label={fieldLabel('color')}
            allLabel={t('À choisir')}
            options={couleurs.map((c) => ({ value: c, label: `${c} · ${parColoris.get(c) ?? 0}/${max}` }))}
            value={coloris ?? ''}
            onChange={(v) => { if (v) setColoris(v); }}
          />
        </div>
      )}
      {couleurs.length > 0 && couleurs.length <= SEUIL_PASTILLES && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 13 }}>
          {couleurs.map((c) => {
            const actif = c === coloris;
            return (
              <button
                key={c} type="button" onClick={() => setColoris(c)} aria-pressed={actif}
                style={{
                  height: 32, padding: '0 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                  border: `1.5px solid var(--${actif ? 'teal-500' : 'ink-200'})`,
                  background: actif ? 'var(--teal-100)' : 'var(--paper)',
                  color: actif ? 'var(--teal-800)' : 'var(--ink-700)',
                  fontSize: '12.5px', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
                }}
              >
                {c}
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10.5px', opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                  {parColoris.get(c) ?? 0}/{max}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ⚠️ CE QUE LA PRÉPARATION A FAIT SE DIT (§23.0) : la photo en base n'est pas le fichier choisi.
          Le taire laisserait croire que l'original y est — et personne ne s'étonnerait d'une qualité
          moindre six mois plus tard, sans pouvoir relier la cause à l'effet. */}
      {prepare !== null && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13, padding: '10px 13px',
          borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', border: '1px solid var(--teal-300)',
          fontSize: '12.5px', color: 'var(--teal-800)', lineHeight: 1.5,
        }}>
          <span style={{ flex: 1 }}>{prepare}</span>
        </div>
      )}

      {failure !== null && (
        <div role="alert" style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 13, padding: '11px 13px',
          borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5,
        }}>
          <span style={{ flex: 1 }}>{failure}</span>
          <button
            type="button" onClick={() => setFailure(null)} aria-label={t('Masquer le message')}
            style={{
              width: 24, height: 24, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent',
              color: 'var(--amber-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, padding: 0, cursor: 'pointer',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      )}

      {rows === null
        ? <p style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Lecture des visuels…')}</p>
        : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(142px, 1fr))', gap: 12 }}>
            {drag.order.map((id, i) => {
              const p = byId.get(id);
              if (!p) return null;
              // Le creux du module remplace la tuile saisie — il porte déjà sa taille et sa mesure.
              if (drag.grabbed === id) return <div key={id}>{drag.placeholder}</div>;
              return (
                <Vignette
                  key={id} photo={p} principale={i === 0} drag={drag} editable={editable}
                  onEtoile={() => void toggleSheetPhoto(id)}
                  onRetire={() => void retirer(id)}
                />
              );
            })}

            {editable && reste > 0 && (
              <label style={{
                aspectRatio: '3 / 4', borderRadius: 'var(--r-sm)', border: '1.5px dashed var(--ink-300)',
                background: 'var(--ink-50)', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
              }}>
                <span style={{ color: 'var(--ink-400)', display: 'flex' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <circle cx="9" cy="10" r="2" />
                    <path d="M21 16l-5-5-4 4-2-2-3 3" />
                  </svg>
                </span>
                <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', fontWeight: 600 }}>{t('Déposer')}</span>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>
                  {t('{n} restante', { n: reste })}
                </span>
                <input type="file" accept="image/*" multiple disabled={envoi}
                  onChange={(e) => void deposer(e.target.files)} style={{ display: 'none' }} />
              </label>
            )}
          </div>
        )}

      {/* Le fantôme du geste se rend tel quel — il se porte tout seul. */}
      {drag.ghost}

      {/* ⚠️ TEXTE RELEVE MOT POUR MOT (maquette l.3906) : il ENONCE la regle des deux notions, que
          rien d autre a l ecran ne dit. Sans lui, « la premiere est la principale du coloris » et
          « l etoile designe la photo de fiche » restent des conventions que personne ne devine. */}
      <div style={{
        marginTop: 14, padding: '13px 15px', borderRadius: 'var(--r-sm)',
        background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6,
      }}>
        <Trans
          k="<1>Limites appliquées à l’ajout</1> — {n} images par coloris."
          vars={{ n: max }}
          parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]}
        /> {/* ⚠️ TOUT-OU-RIEN, et l'écran le DIT (audit 2026-08-25) : il annonçait « les
        autres passent », alors que le premier fichier hors limites interrompt le lot ENTIER. */}
        
        <Trans
          k="Vos photos sont <1>automatiquement redimensionnées à {cible} px</1> et recompressées avant l’envoi (environ {poids} Ko par image) : déposez-les telles qu’elles sortent du téléphone. JPEG, PNG et WebP ; le HEIC d’un iPhone doit être exporté en JPEG. Un fichier que le serveur refuse malgré tout annule l’envoi ENTIER, avec sa raison. Glissez les vignettes pour réordonner — la première est la principale du coloris. L’étoile désigne la photo de fiche, affichée dans la liste des articles."
          vars={{ cible: PHOTO_CIBLE_PX, poids: PHOTO_MAX_KO }}
          parts={[(x) => <strong>{x}</strong>]}
        />
      </div>

      {rows !== null && duColoris.length === 0 && (
        <p style={{ margin: '13px 0 0', fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          
          {t('Aucun visuel pour ce coloris. La première photo déposée deviendra le visuel principal.')}
        </p>
      )}
    </div>
  );
}

/**
 * Une vignette — l'image, son rang, et les deux gestes qui la concernent.
 * ⚠️ TOUTE LA TUILE EST LA PRISE (pas de poignée dédiée) : sur une grille d'images, l'image EST
 * l'objet qu'on déplace, et une poignée obligerait à viser un coin de 20 px.
 */
function Vignette({ photo, principale, drag, editable, onEtoile, onRetire }: {
  photo: ArticlePhoto;
  principale: boolean;
  drag: ReturnType<typeof useTileDrag>;
  editable: boolean;
  /** Bascule le drapeau « photo du modele » — ne deplace RIEN. */
  onEtoile: () => void;
  onRetire: () => void;
}) {
  const tile = drag.tile(photo.id);
  const h = editable ? drag.handle(photo.id) : null;
  return (
    <div
      {...tile}
      {...(h ? { onPointerDown: h.onPointerDown } : {})}
      style={{
        ...tile.style, ...(h?.style ?? {}),
        ...(editable ? { cursor: 'grab', touchAction: 'none', userSelect: 'none' } : null),
      }}
    >
      <div style={{
        position: 'relative', aspectRatio: '3 / 4', borderRadius: 'var(--r-sm)',
        border: `1.5px solid var(--${principale ? 'teal-400' : 'ink-200'})`,
        background: 'var(--ink-50)', overflow: 'hidden',
      }}>
        <div
          role="img" aria-label={principale ? t('Photo principale du coloris') : t('Visuel')}
          style={{
            position: 'absolute', inset: 0, backgroundImage: `url(${photo.url ?? ''})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
          }}
        />
        {/* ⚠️ LA PASTILLE DIT CE QUE LE RANG SIGNIFIE. Sans elle, « première tuile » est une convention
            que rien n'énonce — et personne ne devine qu'un glisser change le visuel de la caisse. */}
        {principale && (
          <span style={{
            position: 'absolute', top: 7, left: 7, height: 20, padding: '0 8px',
            borderRadius: 'var(--r-pill)', background: 'var(--ink-900)', color: 'var(--paper)',
            fontSize: 'var(--t-sub)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
          }}>{t('Principale')}</span>
        )}

        {/* ⚠️ LA SECONDE PASTILLE, EN BAS ET EN AMBRE (relevé maquette l.3877). Sans elle, rien ne dit
            QUELLE vignette part dans la liste d'articles : l'étoile allumée ne se lit que sur la tuile
            qu'on regarde, pas d'un coup d'œil sur la grille entière. */}
        {photo.isMain && (
          <span style={{
            position: 'absolute', bottom: 7, left: 7, height: 20, padding: '0 8px',
            borderRadius: 'var(--r-pill)', background: 'oklch(0.78 0.13 70)', color: '#131618',
            fontSize: 'var(--t-micro)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
          }}>{t('Photo de fiche')}</span>
        )}

        {editable && (
          <>
            {/* ⚠️ L'ÉTOILE NE DÉPLACE RIEN : elle bascule le drapeau « photo du modèle ». Le premier de
                chaque coloris reste désigné par l'ORDRE — deux réglages distincts, deux gestes. */}
            <TileAction
              droite={38}
              actif={photo.isMain}
              heading={photo.isMain ? t('Photo de la fiche — cliquer pour retirer') : t('Choisir comme photo de la fiche')}
              onAction={onEtoile}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill={photo.isMain ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                <path d="M12 3.5l2.5 5.2 5.7.8-4.1 4 1 5.7-5.1-2.7-5.1 2.7 1-5.7-4.1-4 5.7-.8z" />
              </svg>
            </TileAction>
            <TileAction droite={7} heading={t('Retirer cette photo')} onAction={onRetire}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </TileAction>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Un bouton posé SUR la vignette.
 * ⚠️ IL ARRÊTE LE `pointerdown` : toute la tuile est la prise du glisser, donc sans ça, appuyer sur
 * l'étoile amorcerait un déplacement au lieu d'un clic. Le seuil de 5 px du module rattraperait un
 * clic net, mais pas une main qui bouge un peu — et l'action passerait une fois sur deux.
 */
function TileAction({ droite, heading, actif, onAction, children }: {
  droite: number; heading: string; actif?: boolean; onAction: () => void; children: React.ReactNode;
}) {
  return (
    <Tooltip content={heading}>
      <button
        type="button"
        aria-label={heading}
        data-no-drag=""
        onClick={(e) => { e.stopPropagation(); onAction(); }}
        style={{
          position: 'absolute', top: 7, right: droite, width: 26, height: 26,
          border: 'none', borderRadius: 'var(--r-xs)', padding: 0, cursor: 'pointer',
          background: actif ? 'var(--amber)' : 'rgba(12,14,16,.62)',
          color: actif ? '#131618' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >{children}</button>
    </Tooltip>
  );
}
